import React, { useState } from 'react';
import { devLog } from '../../../../utils/devLog';
import { X, Clock, Check } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { toLocalYMD } from '../../../../utils/dateFmt';
import { usePermissions } from '../../../../hooks/usePermissions';
import { Field } from '../../../catalyst/fieldset';
import { SafeLabel as Label } from '../../../ui/SafeLabel';
import { Select } from '../../../catalyst/select';
import { Input } from '../../../catalyst/input';
import DateSelectionCalendar from './DateSelectionCalendar';
import CrewSelectionPanel from './CrewSelectionPanel';

// FT-156: Work-based patterns - user enters WORK periods (more intuitive)
// Rest hours calculated automatically: restHours = 24 - workHours
const WORK_PATTERNS = {
  'day-8': { name: '8-hour day shift (09:00-17:00)', periods: [{ start: '09:00', end: '17:00' }] },
  'day-12': { name: '12-hour day shift (06:00-18:00)', periods: [{ start: '06:00', end: '18:00' }] },
  'night-12': { name: '12-hour night shift (18:00-06:00)', periods: [{ start: '18:00', end: '06:00' }] },
  'split-watch': { name: 'Split watch (6 on/6 off)', periods: [{ start: '06:00', end: '12:00' }, { start: '18:00', end: '00:00' }] },
  'day-off': { name: 'Day off (no work)', periods: [] },
  'custom': { name: 'Custom times', periods: [{ start: '', end: '' }] }
};

const HoursRecordView = ({ onBack, crew = [], existingData = {}, initialSelectedCrew = [], onSave }) => {
  const { currentUser } = usePermissions();

  // Use local date string to avoid timezone issues
  const getLocalDateString = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [selectedDates, setSelectedDates] = useState([getLocalDateString()]);
  const [selectedCrew, setSelectedCrew] = useState(initialSelectedCrew);
  const [selectedPattern, setSelectedPattern] = useState('day-8');
  const [workPeriods, setWorkPeriods] = useState(WORK_PATTERNS['day-8'].periods);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState(0);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [validationWarnings, setValidationWarnings] = useState([]);

  // Deviation form state for non-compliant patterns
  const [showDeviationForm, setShowDeviationForm] = useState(false);
  const [deviation, setDeviation] = useState({
    reason: '',
    description: '',
    approvedBy: '',
    approvalDate: new Date().toISOString().split('T')[0]
  });

  // Use existing data passed from parent instead of loading from storage
  const hoursData = existingData;

  // Show all non-archived crew (includes both On Duty and Off Duty)
  const activeCrew = crew.filter(c => c.status !== 'archived');

  // Handle pattern change
  const handlePatternChange = (pattern) => {
    setSelectedPattern(pattern);
    if (pattern !== 'custom') {
      const newPeriods = [...WORK_PATTERNS[pattern].periods];
      setWorkPeriods(newPeriods);
      checkValidation(newPeriods);
    }
  };

  // Add/remove work period handlers (FT-156: renamed from rest)
  const addWorkPeriod = () => {
    if (workPeriods.length < 2) {
      const updated = [...workPeriods, { start: '', end: '' }];
      setWorkPeriods(updated);
      setSelectedPattern('custom');
      checkValidation(updated);
    }
  };

  const removeWorkPeriod = (index) => {
    const updated = workPeriods.filter((_, i) => i !== index);
    setWorkPeriods(updated);
    setSelectedPattern('custom');
    checkValidation(updated);
  };

  const updateWorkPeriod = (index, field, value) => {
    const updated = [...workPeriods];
    updated[index][field] = value;
    setWorkPeriods(updated);
    setSelectedPattern('custom');
    checkValidation(updated);
  };

  // Real-time validation - runs on every period change
  const checkValidation = (periods) => {
    // Filter out empty periods (user hasn't filled in yet)
    const filledPeriods = periods.filter(p => p.start && p.end);

    // No warnings if no periods filled yet
    if (filledPeriods.length === 0) {
      setValidationWarnings([]);
      return;
    }

    // Run full validation using existing function
    const validation = validateRestPattern(filledPeriods);

    if (!validation.valid) {
      // Extract first line of error message for inline display
      const warningText = validation.error.split('\n')[0];
      setValidationWarnings([warningText]);
    } else {
      // Pattern is valid - clear warnings
      setValidationWarnings([]);
    }
  };

  // Toggle crew selection
  const toggleCrewSelection = (memberId) => {
    if (selectedCrew.includes(memberId)) {
      setSelectedCrew(selectedCrew.filter(id => id !== memberId));
    } else {
      setSelectedCrew([...selectedCrew, memberId]);
    }
  };

  // Select/deselect all crew
  const selectAllCrew = () => {
    if (selectedCrew.length === activeCrew.length) {
      setSelectedCrew([]);
    } else {
      setSelectedCrew(activeCrew.map(m => m.id));
    }
  };

  // Toggle date selection
  const toggleDate = (date) => {
    const dateStr = getLocalDateString(date);
    if (selectedDates.includes(dateStr)) {
      setSelectedDates(selectedDates.filter(d => d !== dateStr));
    } else {
      setSelectedDates([...selectedDates, dateStr]);
    }
  };

  // FT-156: Calculate hours from WORK periods (not rest)
  // restHours = 24 - sum(workPeriods)
  const calculateHoursFromPeriods = (periods) => {
    let totalWorkHours = 0;

    periods.forEach(period => {
      if (period.start && period.end) {
        const startTime = new Date(`2000-01-01T${period.start}`);
        let endTime = new Date(`2000-01-01T${period.end}`);

        // Handle overnight shifts (e.g., 18:00-06:00)
        if (endTime <= startTime) {
          endTime = new Date(`2000-01-02T${period.end}`);
        }

        const workMillis = endTime - startTime;
        totalWorkHours += workMillis / (1000 * 60 * 60);
      }
    });

    const restHours = 24 - totalWorkHours;
    return { workHours: totalWorkHours, restHours };
  };

  // FT-156: Validate work pattern against MGN 505 requirements
  // Now validates WORK periods - max 14h work per day
  const validateWorkPattern = (workPeriods) => {
    // Day off (no work periods) is always valid
    if (workPeriods.length === 0) {
      return { valid: true };
    }

    // Calculate total work hours
    let totalWorkHours = 0;
    workPeriods.forEach(period => {
      if (period.start && period.end) {
        const startTime = new Date(`2000-01-01T${period.start}`);
        let endTime = new Date(`2000-01-01T${period.end}`);
        if (endTime <= startTime) {
          endTime = new Date(`2000-01-02T${period.end}`);
        }
        totalWorkHours += (endTime - startTime) / (1000 * 60 * 60);
      }
    });

    // MGN 505: Maximum 14 hours work in any 24-hour period
    if (totalWorkHours > 14) {
      return {
        valid: false,
        error: `MGN 505 Requirement: Maximum 14 hours work in any 24-hour period.\n\nCurrent work: ${totalWorkHours.toFixed(1)} hours`
      };
    }

    // Check resulting rest hours (must be >= 10h)
    const restHours = 24 - totalWorkHours;
    if (restHours < 10) {
      return {
        valid: false,
        error: `MGN 505 Requirement: Minimum 10 hours rest in any 24-hour period.\n\nCurrent rest: ${restHours.toFixed(1)} hours (work: ${totalWorkHours.toFixed(1)}h)`
      };
    }

    return { valid: true };
  };

  // Keep old name as alias for backward compatibility
  const validateRestPattern = validateWorkPattern;

  // Save hours data with double-save guard
  const handleSave = async () => {
    if (selectedCrew.length === 0 || selectedDates.length === 0) {
      return;
    }

    // VALIDATION: Check MGN 505 requirements
    const validation = validateWorkPattern(workPeriods);
    if (!validation.valid) {
      // Show deviation form instead of blocking - allows documented exceptions
      setShowDeviationForm(true);
      return; // Modal will handle the save with deviation
    }

    // Prevent double-save within 2 seconds
    const now = Date.now();
    if (isSubmitting || (now - lastSaveTime < 2000)) {
      devLog('[HOR] Save blocked - too soon after last save');
      return;
    }

    setIsSubmitting(true);
    setLastSaveTime(now);

    const newHoursData = { ...hoursData };

    selectedCrew.forEach(crewId => {
      if (!newHoursData[crewId]) {
        newHoursData[crewId] = {};
      }

      selectedDates.forEach(date => {
        const { workHours, restHours } = calculateHoursFromPeriods(workPeriods);

        newHoursData[crewId][date] = {
          workPeriods: workPeriods.filter(p => p.start && p.end), // FT-156: store work periods
          workHours,
          restHours,
          totalRest: restHours, // For compatibility
          totalWork: workHours, // For compatibility
          pattern: selectedPattern,
          recordedAt: toLocalYMD(new Date()),
          recordedBy: currentUser?.name || 'Unknown User'
        };
      });
    });
    
    // Call the parent's onSave callback to save through the domain hook
    let result = false;
    let errorMessage = 'Failed to save hours. Please try again.';
    
    if (onSave) {
      try {
        result = await onSave(newHoursData);
        // Check if result is an object with error message
        if (typeof result === 'object' && result.error) {
          errorMessage = result.error;
        }
      } catch (error) {

        result = false;
      }
    } else {

    }
    
    // Determine success - handle both boolean and object returns
    const success = typeof result === 'boolean' ? result : result?.success;
    
    if (success) {
      setTimeout(() => {
        setIsSubmitting(false);
        onBack();
      }, 500);
    } else {
      setIsSubmitting(false);
      toast.error(
        errorMessage
          ? `Unable to save hours: ${errorMessage}. Please check your internet connection and try again.`
          : 'Failed to save hours. Please check your internet connection and try again. If the problem persists, contact support.',
        { duration: 6000 }
      );
    }
  };

  // Handle deviation form submission - saves record WITH deviation documentation
  const handleDeviationSubmit = async () => {
    // Validate deviation form fields
    if (!deviation.reason || !deviation.description || deviation.description.length < 20 || !deviation.approvedBy) {
      toast.error(
        'Please complete all required deviation fields:\n\n' +
        '• Reason for exception\n' +
        '• Description (minimum 20 characters)\n' +
        '• Approved By (name and title)\n\n' +
        'Deviations require documentation for Port State Control compliance.',
        { duration: 8000 }
      );
      return;
    }

    // Prevent double-submit
    if (isSubmitting) {
      devLog('[HOR] Deviation submit blocked - already submitting');
      return;
    }

    setIsSubmitting(true);

    // Build new hours data WITH deviation
    const newHoursData = { ...hoursData };

    selectedCrew.forEach(crewId => {
      if (!newHoursData[crewId]) {
        newHoursData[crewId] = {};
      }

      selectedDates.forEach(date => {
        const { workHours, restHours } = calculateHoursFromPeriods(workPeriods);

        newHoursData[crewId][date] = {
          workPeriods: workPeriods.filter(p => p.start && p.end), // FT-156: store work periods
          workHours,
          restHours,
          totalRest: restHours,
          totalWork: workHours,
          pattern: selectedPattern,
          recordedAt: toLocalYMD(new Date()),
          recordedBy: currentUser?.name || 'Unknown User',
          // DEVIATION DATA - for PSC inspection defense
          deviation: {
            reason: deviation.reason,
            description: deviation.description,
            approvedBy: deviation.approvedBy,
            approvalDate: deviation.approvalDate,
            recordedAt: toLocalYMD(new Date())
          }
        };
      });
    });

    // Save through parent callback
    let result = false;
    let errorMessage = 'Failed to save. Please try again.';

    if (onSave) {
      try {
        result = await onSave(newHoursData);
        if (typeof result === 'object' && result.error) {
          errorMessage = result.error;
        }
      } catch (error) {

        result = false;
      }
    }

    const success = typeof result === 'boolean' ? result : result?.success;

    if (success) {
      // Close modal and reset form
      setShowDeviationForm(false);
      setDeviation({
        reason: '',
        description: '',
        approvedBy: '',
        approvalDate: new Date().toISOString().split('T')[0]
      });

      // Navigate back after brief delay
      setTimeout(() => {
        setIsSubmitting(false);
        onBack();
      }, 500);
    } else {
      setIsSubmitting(false);
      toast.error(
        errorMessage
          ? `Unable to save deviation: ${errorMessage}. Check your connection and try again.`
          : 'Failed to save deviation. Please check your internet connection and try again. Deviation records are required for regulatory compliance.',
        { duration: 6000 }
      );
    }
  };

  return (
    // FT-364: pb-24 md:pb-0 prevents Save Hours button from being
    // hidden behind fixed MobileBottomBar (~64px + safe-area) on mobile.
    // Same FT-83 pattern applied to Crew / Defects / Maintenance forms.
    <div data-testid="hours-record-view-root" className="min-h-screen bg-gray-50 pb-24 md:pb-0">
      {/* Compact Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={onBack}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
                Record Hours of Rest
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="space-y-6">
          {/* Top Row: Date Selection and Crew Selection */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <DateSelectionCalendar
              selectedDates={selectedDates}
              onToggleDate={toggleDate}
              hoursData={hoursData}
              selectedCrew={selectedCrew}
              currentMonth={currentMonth}
              onMonthChange={setCurrentMonth}
            />

            <CrewSelectionPanel
              activeCrew={activeCrew}
              selectedCrew={selectedCrew}
              onToggleCrewSelection={toggleCrewSelection}
              onSelectAllCrew={selectAllCrew}
            />
          </div>

          {/* FT-156: Work Periods (user enters work times, rest is calculated) */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <Clock className="h-5 w-5 mr-2 text-gray-500" />
                Work Periods
              </h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <Field>
                  <Label>Quick Pattern</Label>
                  <Select value={selectedPattern} onChange={(e) => handlePatternChange(e.target.value)}>
                    {Object.entries(WORK_PATTERNS).map(([key, pattern]) => (
                      <option key={key} value={key}>{pattern.name}</option>
                    ))}
                  </Select>
                </Field>

                {/* Work Period Inputs */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Work Times</Label>
                    {workPeriods.length < 2 ? (
                      <button
                        type="button"
                        onClick={addWorkPeriod}
                        className="text-sm text-cyan-600 hover:text-cyan-500"
                      >
                        + Add Period
                      </button>
                    ) : (
                      <span
                        className="text-sm text-gray-400 cursor-not-allowed"
                        title="Maximum 2 work periods allowed per day"
                      >
                        + Add Period (max 2)
                      </span>
                    )}
                  </div>

                  {workPeriods.map((period, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <Input
                        type="time"
                        value={period.start}
                        onChange={(e) => updateWorkPeriod(index, 'start', e.target.value)}
                        placeholder="Start"
                        className="flex-1"
                      />
                      <span className="text-gray-500">to</span>
                      <Input
                        type="time"
                        value={period.end}
                        onChange={(e) => updateWorkPeriod(index, 'end', e.target.value)}
                        placeholder="End"
                        className="flex-1"
                      />
                      {workPeriods.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeWorkPeriod(index)}
                          className="text-red-600 hover:text-red-500"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}

                  <p className="text-sm text-gray-500">
                    MGN 505: Maximum 14h work per day. Rest hours calculated automatically.
                  </p>
                </div>
              </div>

              {(() => {
                const hours = calculateHoursFromPeriods(workPeriods);
                return hours && (
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-600 font-medium">Work Hours</p>
                    <p className="text-2xl font-bold text-blue-900">{hours.workHours.toFixed(1)}h</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-600 font-medium">Rest Hours</p>
                    <p className="text-2xl font-bold text-green-900">{hours.restHours.toFixed(1)}h</p>
                  </div>
                  {hours.workHours > 14 && (
                    <div className="col-span-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-800">
                        ⚠️ Warning: More than 14 hours of work per day may not comply with MGN 505 regulations
                      </p>
                    </div>
                  )}
                </div>
                );
              })()}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="space-y-4">
              {/* Real-time Validation Warnings */}
              {validationWarnings.length > 0 && (
                <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3 flex-1">
                      <h3 className="text-sm font-medium text-amber-800">
                        MGN 505 Validation Warning
                      </h3>
                      <div className="mt-2 text-sm text-amber-700 space-y-1">
                        {validationWarnings.map((warning, index) => (
                          <p key={index}>• {warning}</p>
                        ))}
                      </div>
                      <p className="mt-2 text-xs text-amber-600">
                        Please correct the issues above before saving. You may document an exception if this pattern is required due to operational circumstances.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Buttons */}
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={onBack}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={selectedCrew.length === 0 || selectedDates.length === 0 || isSubmitting}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-md flex items-center gap-2 transition-colors
                    ${validationWarnings.length > 0
                      ? 'bg-amber-500 hover:bg-amber-600'
                      : 'bg-cyan-600 hover:bg-cyan-700'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  title={validationWarnings.length > 0 ? 'Validation warnings present - save will prompt for deviation documentation' : ''}
                >
                  {isSubmitting ? (
                    <>Saving...</>
                  ) : validationWarnings.length > 0 ? (
                    <>
                      <Check className="h-4 w-4" />
                      Save with Exception...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4" />
                      Save Hours
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Deviation Documentation Modal */}
      {showDeviationForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-auto max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-red-50">
              <h3 className="text-lg font-semibold text-gray-900">
                Document Non-Compliance Exception
              </h3>
              <p className="mt-2 text-sm text-gray-700">
                This rest pattern does not meet MGN 505 (M) Amendment 1 requirements.
                To proceed with saving this record, you must document the reason for this exception.
              </p>
              <p className="mt-1 text-xs text-red-600 font-medium">
                Required for Port State Control (PSC) inspection defense and SMS compliance.
              </p>
            </div>

            {/* Modal Body - Form Fields */}
            <div className="px-6 py-4 space-y-5">
              {/* Reason Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for Exception <span className="text-red-500">*</span>
                </label>
                <select
                  value={deviation.reason}
                  onChange={(e) => setDeviation({ ...deviation, reason: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm"
                  required
                >
                  <option value="">Select reason...</option>
                  <option value="Emergency">Emergency (e.g., machinery failure, flooding, fire)</option>
                  <option value="Drill/Exercise">Drill or Exercise (e.g., fire drill, lifeboat drill)</option>
                  <option value="Port Operations">Port Operations (e.g., arrival, departure, cargo ops)</option>
                  <option value="Other">Other (explain in description)</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Select the category that best describes why this deviation occurred.
                </p>
              </div>

              {/* Description Textarea */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Detailed Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={deviation.description}
                  onChange={(e) => setDeviation({ ...deviation, description: e.target.value })}
                  rows={5}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm"
                  placeholder="Explain what happened, why the deviation was necessary, and what actions were taken. Be specific - this will be reviewed during PSC inspections."
                  required
                />
                <div className="mt-1 flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    Minimum 20 characters required for meaningful explanation
                  </p>
                  <p className={`text-xs font-medium ${deviation.description.length >= 20 ? 'text-green-600' : 'text-red-600'}`}>
                    {deviation.description.length} / 20 characters
                  </p>
                </div>
              </div>

              {/* Approved By Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Approved By (Name and Title) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={deviation.approvedBy}
                  onChange={(e) => setDeviation({ ...deviation, approvedBy: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm"
                  placeholder="e.g., Captain John Smith, Master"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  Enter the name and title of the officer who approved this deviation.
                </p>
              </div>

              {/* Approval Date Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Approval Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={deviation.approvalDate}
                  onChange={(e) => setDeviation({ ...deviation, approvalDate: e.target.value })}
                  max={new Date().toISOString().split('T')[0]}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  Date when this deviation was approved. Defaults to today.
                </p>
              </div>

              {/* Information Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <p className="text-xs text-blue-800 leading-relaxed">
                  <strong>PSC Inspection Note:</strong> This documentation provides evidence that your SMS is
                  working correctly by identifying and documenting non-conformances. Ensure the description
                  clearly explains the operational necessity for this deviation.
                </p>
              </div>
            </div>

            {/* Modal Footer - Action Buttons */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeviationForm(false);
                  setDeviation({
                    reason: '',
                    description: '',
                    approvedBy: '',
                    approvalDate: new Date().toISOString().split('T')[0]
                  });
                  setIsSubmitting(false);
                }}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleDeviationSubmit}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>Saving...</>
                ) : (
                  <>
                    <Check className="h-4 w-4" />
                    Submit Deviation & Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HoursRecordView;